import { Button, Card, Input, Spin, Alert, Divider, Tag, Space, Descriptions, List, Modal, Table, TableColumnsType, Image } from "antd";
import { useState } from "react";
import { SearchOutlined, ReloadOutlined, PhoneOutlined, SyncOutlined } from "@ant-design/icons";
import useStuckDrivers from "hooks/stuckDrivers/useStuckDrivers";
import useDriverSearch from "hooks/drivers/useDriverSearch";
import { useNotificationContext } from "contexts/NotificationContext";
import { IStuckDriverDiagnosis } from "models/location";
import { IDriver } from "models/driver";
import { IFleet } from "models/fleet";
import moment from "moment";


export default function StuckDrivers() {
  const [driverId, setDriverId] = useState<string>("");
  const [phoneSearch, setPhoneSearch] = useState<string>("");
  const [isSearchModalVisible, setIsSearchModalVisible] = useState<boolean>(false);
  const { showError } = useNotificationContext();
  const { loading, diagnosis, diagnoseDriver, resetDriverLocation, autoCleanupStuckDrivers } = useStuckDrivers();
  const {
    data: { data: searchResults, loading: searchLoading },
    handleSearch: handlePhoneSearch,
  } = useDriverSearch();

  // Driver search columns for the modal
  const driverSearchColumns: TableColumnsType<IDriver> = [
    {
        title: "Photo",
        dataIndex: "image",
        width: 80,
        render: (image: string) => (
            <Image
                height={40}
                width={40}
                className="rounded object-cover"
                src={image}
                preview={false}
            />
        ),
    },
    {
        title: "Name",
        render: (driver: IDriver) => (
            <div>
                <div className="font-medium">
                    {`${driver.first_name} ${driver.middle_name || ''} ${driver.last_name}`.trim()}
                </div>
                <div className="text-xs text-gray-500">
                    ID: {driver.id.slice(-8)}...
                </div>
            </div>
        ),
    },
    {
        title: "Phone",
        dataIndex: "phone",
        render: (phone: string) => (
            <div className="font-mono text-sm">{phone}</div>
        ),
    },
    {
        title: "Plate Number",
        dataIndex: "fleet",
        render: (fleet: IFleet) => (
            <div className="font-mono text-sm">{fleet?.plate_number || 'N/A'}</div>
        ),
    },
    {
        title: "Status",
        render: (driver: IDriver) => (
            <Space>
                <Tag color={driver.is_active ? "green" : "red"}>
                    {driver.is_active ? "Active" : "Inactive"}
                </Tag>
                <Tag color={driver.is_verified ? "blue" : "orange"}>
                    {driver.is_verified ? "Verified" : "Unverified"}
                </Tag>
            </Space>
        ),
    },
    {
        title: "Actions",
        width: 150,
        render: (driver: IDriver) => (
            <Space>
                <Button
                    size="small"
                    type="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSelectDriver(driver);
                    }}
                >
                    Select & Diagnose
                </Button>
                <Button
                    size="small"
                    danger
                    onClick={async (e) => {
                        e.stopPropagation();
                        // Quick reset without diagnosis
                        await resetDriverLocation({
                            driver_id: driver.id,
                            latitude: 9.0192, // Addis Ababa center
                            longitude: 38.7525,
                            heading: 0.0,
                        });
                    }}
                    loading={loading}
                >
                    Quick Reset
                </Button>
            </Space>
        ),
    },
  ];

  const handleDiagnose = async () => {
    if (!driverId.trim()) return;
    await diagnoseDriver(driverId.trim());
  };

  const handlePhoneSearchChange = (value: string) => {
    setPhoneSearch(value);
    handlePhoneSearch({ target: { value } } as any);
  };

  const handleSelectDriver = (driver: IDriver) => {
    setDriverId(driver.id);
    setIsSearchModalVisible(false);
    setPhoneSearch("");
  };

  const showSearchModal = () => {
    setIsSearchModalVisible(true);
  };

  const hideSearchModal = () => {
    setIsSearchModalVisible(false);
    setPhoneSearch("");
  };

    const handleResetLocation = async () => {
        if (!diagnosis) {
            showError("No driver selected for reset");
            return;
        }

        console.log("🚀 Starting location reset for driver:", diagnosis.driver_id);
        const success = await resetDriverLocation({
            driver_id: diagnosis.driver_id,
            latitude: 9.0192, // Addis Ababa center
            longitude: 38.7525,
            heading: 0.0,
        });

        if (success) {
            console.log("✅ Location reset completed successfully");
        } else {
            console.log("❌ Location reset failed");
        }
    };

    const renderDiagnosisCard = (diagnosis: IStuckDriverDiagnosis) => (
            <Card
                title={
                    <div className="flex items-center gap-2">
                        <span>Driver Diagnosis</span>
                        {diagnosis.is_stuck ? (
                            <Tag color="red">STUCK</Tag>
                        ) : (
                            <Tag color="green">NORMAL</Tag>
                        )}
                    </div>
                }
                className="mt-6"
            >
            <Descriptions bordered column={2}>
                <Descriptions.Item label="Driver ID">{diagnosis.driver_id}</Descriptions.Item>
                <Descriptions.Item label="Name">{diagnosis.driver_name}</Descriptions.Item>
                <Descriptions.Item label="Phone">{diagnosis.phone}</Descriptions.Item>
                <Descriptions.Item label="Status">
                    <Tag color={diagnosis.current_location.status === "online" ? "green" : "orange"}>
                        {diagnosis.current_location.status.toUpperCase()}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Current Location">
                    {diagnosis.current_location.latitude.toFixed(6)}, {diagnosis.current_location.longitude.toFixed(6)}
                </Descriptions.Item>
                <Descriptions.Item label="Heading">{diagnosis.current_location.heading}°</Descriptions.Item>
                <Descriptions.Item label="Last Update">
                    {moment(diagnosis.current_location.updated_at).format("lll")}
                </Descriptions.Item>
                <Descriptions.Item label="Ongoing Trips">{diagnosis.ongoing_trips_count}</Descriptions.Item>
                <Descriptions.Item label="Recent Updates">{diagnosis.recent_location_updates}</Descriptions.Item>
            </Descriptions>

            {diagnosis.is_stuck && (
                <Alert
                    message="Driver is Stuck"
                    description={
                        <div>
                            <p>This driver appears to be stuck. Issues detected:</p>
                            <List
                                size="small"
                                dataSource={diagnosis.issues}
                                renderItem={(item) => <List.Item>• {item}</List.Item>}
                            />
                        </div>
                    }
                    type="error"
                    showIcon
                    className="mt-4"
                />
            )}

            {!diagnosis.is_stuck && (
                <Alert
                    message="Driver is Normal"
                    description="This driver appears to be functioning normally."
                    type="success"
                    showIcon
                    className="mt-4"
                />
            )}

            {diagnosis.last_5_updates.length > 0 && (
                <div className="mt-4">
                    <h4 className="font-semibold mb-2">Last 5 Location Updates:</h4>
                    <List
                        size="small"
                        dataSource={diagnosis.last_5_updates}
                        renderItem={(update, index) => (
                            <List.Item>
                                <div className="flex justify-between w-full">
                                    <span>
                                        {index + 1}. {update.latitude.toFixed(6)}, {update.longitude.toFixed(6)}
                                    </span>
                                    <span className="text-gray-500">
                                        {moment(update.updated_at).format("HH:mm:ss")}
                                    </span>
                                </div>
                            </List.Item>
                        )}
                    />
                </div>
            )}

            <div className="mt-4">
                <Divider />
                <div className="text-center space-y-3">
                    <div className="flex gap-3 justify-center">
                        <Button
                            type="default"
                            icon={<SyncOutlined />}
                            loading={loading}
                            onClick={() => handleDiagnose()}
                            size="large"
                        >
                            Refresh Diagnosis
                        </Button>
                        <Button
                            type="primary"
                            danger
                            icon={<ReloadOutlined />}
                            loading={loading}
                            onClick={handleResetLocation}
                            size="large"
                        >
                            Reset Driver Location
                        </Button>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                        <p><strong>Reset Location:</strong> Force reset this driver's location to Addis Ababa center (9.0192, 38.7525) and set status to online.</p>
                        <p><strong>Dashboard Map:</strong> Updates automatically every 4 seconds. Manual refresh may be needed if changes don't appear immediately.</p>
                    </div>
                </div>
            </div>
        </Card>
    );

  return (
    <>
    <div className="w-full p-6">
        <div className="max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Stuck Drivers Management</h1>
                <p className="text-gray-600">
                    Diagnose and fix drivers that appear to be stuck in one location or have outdated location data.
                </p>
            </div>

                <Card 
                    title={
                        <div className="flex justify-between items-center">
                            <span>Fleet-wide Optimization</span>
                            <Tag color="blue">Purge Zombie Drivers</Tag>
                        </div>
                    } 
                    className="mb-6 border-blue-100 bg-blue-50/10"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex-1 pr-6">
                            <p className="text-sm text-gray-600 mb-0">
                                This will automatically identify and force offline all drivers who haven't updated their location in over 5 minutes. 
                                It ensures the dispatch engine only sees active, healthy drivers.
                            </p>
                        </div>
                        <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            loading={loading}
                            onClick={async () => {
                                Modal.confirm({
                                    title: 'Run Fleet-wide Cleanup?',
                                    content: 'This will scan all online drivers and force offline those with stale heartbeats. This is safe to run during peak hours.',
                                    okText: 'Run Cleanup',
                                    okType: 'primary',
                                    onOk: async () => {
                                        await autoCleanupStuckDrivers();
                                    }
                                });
                            }}
                        >
                            Run Fleet Cleanup
                        </Button>
                    </div>
                </Card>

                <Card title="Diagnose Individual Driver" className="mb-6">
                    <div className="space-y-4">
                        <div>
                            <Space.Compact style={{ width: '100%' }}>
                                <Input
                                    placeholder="Enter Driver ID (UUID)"
                                    value={driverId}
                                    onChange={(e) => setDriverId(e.target.value)}
                                    onPressEnter={handleDiagnose}
                                    style={{ width: 'calc(100% - 100px)' }}
                                />
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    loading={loading}
                                    onClick={handleDiagnose}
                                    disabled={!driverId.trim()}
                                >
                                    Diagnose
                                </Button>
                            </Space.Compact>
                        </div>

                        <Divider plain>or</Divider>

                        <div>
                            <Button
                                type="default"
                                icon={<PhoneOutlined />}
                                onClick={showSearchModal}
                                className="w-full"
                            >
                                Search Driver by Phone Number
                            </Button>
                        </div>
                    </div>

                    <div className="mt-2 text-sm text-gray-500">
                        Enter the driver's UUID directly, or search for drivers by phone number to find their UUID.
                    </div>
                </Card>

                {loading && !diagnosis && (
                    <div className="text-center py-8">
                        <Spin size="large" />
                        <p className="mt-2 text-gray-600">Diagnosing driver...</p>
                    </div>
                )}

                {diagnosis && renderDiagnosisCard(diagnosis)}
            </div>
        </div>

        {/* Driver Search Modal */}
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <PhoneOutlined />
                    <span>Search Driver by Phone Number</span>
                </div>
            }
            open={isSearchModalVisible}
            onCancel={hideSearchModal}
            footer={null}
            width={800}
        >
            <div className="space-y-4">
                <Input
                    placeholder="Enter phone number (e.g., +251911234567 or 911234567)"
                    prefix={<PhoneOutlined />}
                    value={phoneSearch}
                    onChange={(e) => handlePhoneSearchChange(e.target.value)}
                    onPressEnter={() => {
                        if (phoneSearch.trim()) {
                            handlePhoneSearchChange(phoneSearch.trim());
                        }
                    }}
                />

                {searchLoading && (
                    <div className="text-center py-4">
                        <Spin size="small" />
                        <p className="mt-2 text-gray-600">Searching drivers...</p>
                    </div>
                )}

                {searchResults.length > 0 && !searchLoading && (
                    <Table
                        size="small"
                        columns={driverSearchColumns}
                        dataSource={searchResults}
                        pagination={false}
                        rowKey="id"
                        onRow={(record) => ({
                            onClick: () => handleSelectDriver(record),
                            className: "cursor-pointer hover:bg-gray-50"
                        })}
                    />
                )}

                {phoneSearch.trim() && searchResults.length === 0 && !searchLoading && (
                    <div className="text-center py-4 text-gray-500">
                        No drivers found with phone number containing "{phoneSearch}"
                    </div>
                )}

                {!phoneSearch.trim() && (
                    <div className="text-center py-4 text-gray-500">
                        Enter a phone number to search for drivers. You can select a driver to diagnose or use Quick Reset.
                    </div>
                )}
            </div>
        </Modal>
    </>
    );
}
